import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { IntegrationStatus, SubscriptionStatus } from "./ApiTransport";
import { Subscription } from "./ApiTypes";
import { showDate } from "./Date";

export interface StatusOption { lbl: string; expl: string; val: SubscriptionStatus; }
export interface IntegrationStatusOption { lbl: string; expl: string; val: IntegrationStatus; }

export const showSubscriptionStatus = ( status: SubscriptionStatus ): string => {
    switch ( status ) {
        case SubscriptionStatus.Applied: return 'Applied';
        case SubscriptionStatus.Rejected: return 'Rejected';
        case SubscriptionStatus.WaitingForContract: return 'Waiting for Contract';
        case SubscriptionStatus.Active:    return 'Active';
        case SubscriptionStatus.Cancelled: return 'Cancelled';
        case SubscriptionStatus.Waitlisted: return 'Waitlisted';
        default:
            return 'Unknown Status';
    }
};

export const reject = {
    lbl: "Reject Application",
    expl: "The application will be rejected",
    val: SubscriptionStatus.Rejected
};

export const accept = {
    lbl: "Accept Application",
    expl: "The plan will be set to Active",
    val: SubscriptionStatus.Active
};

export const addToWaitlist = {
    lbl: "Add To Waitlist",
    expl: "The application will be placed on the waitlist. You can invite them from the waitlist tab once spaces become available",
    val: SubscriptionStatus.Waitlisted
};

export const undoReject = {
    lbl:  "Undo Reject Application",
    expl: "Set to Applied",
    val: SubscriptionStatus.Applied
};

export const undoAccept = {
    lbl:  "Undo Accept Application",
    expl: "Set to Applied",
    val: SubscriptionStatus.Applied
};

export const acceptContract = {
    lbl:  "Accept Contract",
    expl: "Set to Active",
    val: SubscriptionStatus.Active
};

export const cancel = {
    lbl:  "Cancel Plan",
    expl: "The Plan will be cancelled and will not renew",
    val: SubscriptionStatus.Cancelled
};

export const reactivate = {
    lbl:  "Reactivate Plan",
    expl: "Set to Active",
    val: SubscriptionStatus.Active
};

export const getInstructions = ( status: SubscriptionStatus, endDate: LocalDate, hasPaymentMethod: boolean, planEndDate: LocalDate | null ): string => {
    if( status == SubscriptionStatus.Applied ) {
        return "The application needs to be reviewed.";
    }
    if( status == SubscriptionStatus.Rejected ) {
        return "The application was rejected.";
    }
    if( status == SubscriptionStatus.WaitingForContract ) {
        return "The application was approved.";
    }
    if( status == SubscriptionStatus.Active && planEndDate ) {
        const formatter = DateTimeFormatter.ofPattern('MM/dd/yyyy');
        return `The plan is active and will end on ${planEndDate.format(formatter)}.`;
    }
    if( status == SubscriptionStatus.Active ) {
        return hasPaymentMethod ? "The plan is active and will auto-renew." : "The plan is active and the parker will be invoiced.";
    }
    if( status == SubscriptionStatus.Cancelled ) {
        const today = LocalDate.now();
      
        let message = "The plan is cancelled and will not renew.";
        if (endDate.isAfter(today)) {
          message += ` But the parker may park for the remainder of your period until ${showDate(endDate)}.`;
        }
      
        return message;
    }
    if (status == SubscriptionStatus.Waitlisted) {
        return "Waitlisted";
    }
    return "Unknown status";
}

export function getActionsInstructions( status: SubscriptionStatus ): string {
    if( status === SubscriptionStatus.Applied ) {
        return "Review the application and accept or reject it";
    }
    if( status === SubscriptionStatus.WaitingForContract ) {
        return "Contract has been sent to customer. Once received, you can accept the contract";
    }
    return "Unknown status";
}

export function filterStatusOptions( status: SubscriptionStatus ): StatusOption[] {
    if( status === SubscriptionStatus.Applied ) {
        return [reject, accept, addToWaitlist];
    }
    if( status === SubscriptionStatus.Rejected ) {
        return [undoReject];
    }
    if( status === SubscriptionStatus.WaitingForContract ) {
        return [acceptContract];
    }
    if (status === SubscriptionStatus.Cancelled) {
      return [cancel];
    }
    if (status === SubscriptionStatus.Active) {
      return [reactivate];
    }
    
    return [];
}

export function isBeforeOrEqual( a: LocalDate, b: LocalDate ) {
    const acond = a.isBefore( b );
    const bcond = b.equals( a );
    return acond || bcond;
}

export function currentSpaces( sub: Subscription ) {
    const now = LocalDate.now();
    const currentQuantity = sub.accessPeriods
                               .filter( ap => isBeforeOrEqual( ap.start, now ) && isBeforeOrEqual( now, ap.end ) )
                               .map( ap => ap.quantity ).reduce( ( a, b ) => a + b, 0 );

    return currentQuantity;
}

export function formatSubscriptionStatus(value: any) {
  if (value === null || value === undefined) return "null";

  const parsed = Number(value);
  if (isNaN(parsed)) return value;

  const statusName = SubscriptionStatus[parsed];
  return statusName ?? value;
}
